/**
 * Action types for the variant selection, addition, and modification.
 */

var sw = require('js/lib/sw');
var swPricing = require('js/lib/sw-pricing-calc');
var ShapewaysModel = require('./ShapewaysModel');
var _ = require('underscore');
var actions = require('js/lib/actions/ProductEditModel/actionTypes');
var actionCreators = require('js/lib/actions/ProductEditModel/actionCreators');
var $ = require('js/sw-jquery/sw-jquery');
var productDiscoverabilityActionCreators = require('js/lib/actions/ProductDiscoverability/actionCreators');

// Constants
var PCONF_INFO_ENDPOINT = '/variants/edit/pricing';
var VARIANT_GROUPING = 'variants';
var PCONF_LIMIT = 770;

var ProductEditModel = ShapewaysModel.extend({
  idAttribute: 'spin',

  pluralName: 'productEditModels',

  actions: {
    'materialSelected':         actions.MODEL_MATERIAL_SELECTED,
    'materialDeselected':       actions.MODEL_MATERIAL_DESELECTED,
    'getModelSelectedInfo':     actions.GET_MODEL_INFO,
    'variantSelected':          actions.VARIANT_SELECTED,
    'variantDeselected':        actions.VARIANT_DESELECTED,
    'variantOptionSelected':    actions.VARIANT_OPTION_SELECTED,
    'variantOptionDeselected':  actions.VARIANT_OPTION_DESELECTED,
    'pconfActivationChanged':   actions.PCONF_ACTIVATION_CHANGED,
    'pconfDefaultChanged':      actions.PCONF_DEFAULT_CHANGED,
    'modelSelected':            actions.MODEL_SELECTED,
    'getMaterialSelectedInfo':  actions.GET_MODEL_MATERIAL_INFO,
    'pconfPricingChanged':      actions.PCONF_PRICING_CHANGED,
    'getVariantOptionInfo':     actions.GET_VARIANT_OPTION_INFO,
    'groupByChanged':           actions.GROUP_BY_CHANGED,
    'contentLicenseChanged':    actions.CONTENT_LICENSE_CHANGED,
    'contentLicenseTagEntered': actions.CONTENT_LICENSE_TAG_ENTERED,
    'resetAcceptance':          actions.CONTENT_LICENSE_RESET_ACCEPTANCE
  },

  defaults: {
    'chosenVariant':          null,
    'chosenVariantOptions':   [],
    'chosenMaterials':        [],
    'contentLicenses':        {},
    'productContentLicense':  {},
    'spin':                   '',
    'materials':              {},
    'materialOrder':          [],
    'variants':               {},
    'currentVariantOptions':  [],
    'productConfigurations':  [],
    'pendingPconfIndex':      0,
    'resetAcceptance':        0,
    'groupBy':                VARIANT_GROUPING,
    'tagContentLicenseIds':   []
  },

  /**
   * Convenience method for getting a list of the action creators this model
   * subscribes to.
   *
   *
   * @return {function}
   */
  getActionCreators: function getActionCreators() {
    return actionCreators;
  },

  /**
   * @param  {object} models
   * @return {object}
   */
  getBaseModel: function getBaseModel(models) {
    return _.findWhere(models, {isBase: true});
  },

  /**
   * @param pconfs array
   * @param materialId string
   * @param variantOptionId string
   */
  getPconf: function getPconf(pconfs, materialId, variantOptionId) {
    var findPconfCriteria = {
      materialId:  materialId
    };

    if (variantOptionId) {
      _.extend(findPconfCriteria, {
        variantOptionId:  variantOptionId
      });
    }
    return _.findWhere(pconfs, findPconfCriteria);
  },

  /**
   * Returns the index of a product configuration given a materialId and variantOptionId
   *
   * @param pconfs array
   * @param materialId string
   * @param variantOptionId string
   */
  getPconfIndex: function getPconfIndex(pconfs, materialId, variantOptionId) {
    return _.findIndex(pconfs, function(pconf) {
      return pconf.materialId === materialId && pconf.variantOptionId === variantOptionId;
    });
  },

  /**
   * @param state object State object that represents the model.
   * @param materialId string
   * @param variantOptionId string
   * @returns {{pconfId: string, modelId: string, materialId: string, materialTitle: string, variantOption: string, variantOptionName: string, spin: string, pending: boolean}}
   */
  generatePendingPconf: function generateBasePconf(state, materialId, variantOptionId) {
    var baseModel = this.getBaseModel(state.models);
    var chosenVariant = state.chosenVariant;

    return {
      pconfId:            'pending_' + state.pendingPconfIndex,
      modelId:            baseModel ? baseModel.id : null,
      materialId:         materialId,
      materialTitle:      materialId ? state.materials[materialId].title : '',
      swatchImagePath:    materialId ? state.materials[materialId].imageUrl : '',
      variantOptionId:    variantOptionId,
      variantOptionName:  chosenVariant && variantOptionId ? state.variants[chosenVariant].options[variantOptionId].title : '',
      spin:               state.spin,
      variantId:          state.chosenVariant,
      pending:            baseModel ? true : false,
      hasAcceptedContentLicense: state.productContentLicense.isAccepted === 1
    };
  },

  /**
   * Updates default attributes (either materials, or variants).
   *
   * @param attrs {object} attributes that need a default
   * @param defaultId ID to make a default.
   * @returns {object} attributes with an updated default.
   */
  updateDefaultAttribute: function updateDefaultAttribute(attrs, defaultId) {
    _.each(attrs, function(chosenAttribute) {
      chosenAttribute.isDefault = defaultId === chosenAttribute.id;
    });

    return attrs;
  },

  /**
   * Returns whether or not a set of product configurations has a default.
   *
   * @param pconfs
   * @returns {boolean|*}
   */
  hasDefaultPconf: function checkNeedsDefaultPconf(pconfs) {
    return (!_.isEmpty(pconfs) && !_.isEmpty(_.findWhere(pconfs, {isDefault: true}))) || _.isEmpty(pconfs);
  },

  /**
   * Gets new default pconf ID for when one does not exist.
   *
   * @param pconfs object
   * @param pconfCriteria object
   * @returns {*}
   */
  getNewDefaultPconfIndex: function getNewDefaultPconf(pconfs, pconfCriteria) {
    pconfCriteria = pconfCriteria || {};
    var pconf = _.findWhere(pconfs, pconfCriteria);
    var pconfIndex = this.getPconfIndex(pconfs, pconf.materialId, pconf.variantOptionId);

    return pconf ? pconfIndex : 0;
  },

  /**
   * Sets the defaults for product configurations, chosen materials, and chosen variant options.
   * @param state {object}
   * @returns {object}
   */
  setDefaults: function(state, pconfCriteria) {
    var clonedState = sw.utils.cloneDataObject(state);

    var newDefaultPconfIndex = this.getNewDefaultPconfIndex(clonedState.productConfigurations, pconfCriteria);
    var newDefaultPconf = clonedState.productConfigurations[newDefaultPconfIndex];

    if (newDefaultPconf) {
      newDefaultPconf.isDefault = true;

      clonedState.chosenMaterials = this.updateDefaultAttribute(clonedState.chosenMaterials, newDefaultPconf.materialId);
      clonedState.chosenVariantOptions = this.updateDefaultAttribute(clonedState.chosenVariantOptions, newDefaultPconf.variantOptionId);
    }

    return clonedState;
  },

  /**
   * Updates a partial row with the rest of its information.
   *
   * @param pconfs {Object} Product configurations.
   * @param partialRow {Object} The pconf info from the partial row.
   * @param existingPconfInfo {Object} The existing pconf information from the server response (checks if a pconf already exists)
   * @param newPconfInfo {Object} New pconf info incase one doesn't already exist.
   * @param additionalParams Additional params to populate the rest of the row.
   * @returns {Object} Product configurations
   */
  completePartialPconfInfo: function completePartialPconfInfo(pconfs, partialRow, existingPconfInfo, newPconfInfo, additionalParams) {
    var clonedPconfs = sw.utils.cloneDataObject(pconfs);

    var pconfInfo = existingPconfInfo ? existingPconfInfo : newPconfInfo;

    _.defaults(partialRow, pconfInfo);
    partialRow.pending = false;
    partialRow.forSale = true;
    partialRow.pconfId = existingPconfInfo ? pconfInfo.pconfId : partialRow.pconfId;

    if (additionalParams) {
      _.extend(partialRow, additionalParams);
    }

    var pconfIndex = this.getPconfIndex(clonedPconfs, partialRow.materialId, partialRow.variantOptionId);
    clonedPconfs[pconfIndex] = partialRow;

    return clonedPconfs;
  },

  /**
   * Finds an existing pconf and completes its partial data.
   *
   * @param state object
   * @param pconfData object pconf info
   * @param materialId string
   * @param optionId string
   */
  updatePartialPconfRow: function updatePartialPconfRow(state, pconfData, materialId, optionId) {
    var clonedState = sw.utils.cloneDataObject(state);
    optionId = optionId ? optionId : '';

    var existingRow = this.getPconf(clonedState.productConfigurations, materialId, optionId);

    // If the row exists, it needs to be updated.
    if (!_.isEmpty(existingRow)) {
      var existingPconf = this.getPconf(pconfData.existingPconfData, materialId, optionId);
      state.productConfigurations = this.completePartialPconfInfo(state.productConfigurations, existingRow, existingPconf, pconfData, {variantOptionId: optionId});
    }

    return clonedState;
  },

  /**
   * Hits the validation endpoint where we get our pconf and model data.
   *
   * @param requestData {object} data to be passed to the endpoint
   * @param dispatchCallback {function} callback that dispatches the needed action
   */
  getInfoAjax: function getPconfInfoAjax(requestData, dispatchCallback) {
    $.swAjax({
      url:      PCONF_INFO_ENDPOINT,
      type:     'POST',
      data:     requestData,
      dataType: 'json',
      success:  dispatchCallback,
      error: function (jqXHR) {
        var response = window.JSON.parse(jqXHR.responseText);
        _.each(response.errors, function(errorMessage) {
          sw.notifications.error(errorMessage);
        });
      }
    });
  },

  /**
   * Makes an Ajax request and dispatches the response info.
   *
   * @param state {object}
   * @param action {object}
   */
  dispatchMaterialSelected: function dispatchMaterialSelected(state, materialIds) {
    var baseModel = this.getBaseModel(state.models);

    var chosenVariants = {};

    if (!_.isEmpty(state.chosenVariant)) {
      chosenVariants[state.chosenVariant] = _.pluck(state.chosenVariantOptions, 'id');
    }

    // Format for sending request data to endpoint.
    var requestData = {
      validationInfo:   {},
      spin:             state.spin,
      contentLicenseId: state.productContentLicense.licenseId,
      contentLicenseAccepted: state.productContentLicense.isAccepted
    };

    _.each(materialIds, function(materialId) {
      requestData.validationInfo[materialId] = {
        materialId:       materialId,
        chosenVariants:   chosenVariants,
        modelId:          baseModel.id
      };
    }, this);

    this.getInfoAjax(requestData, function(responseData) {
      sw.dispatch(actionCreators.modelMaterialSelected(materialIds, responseData));
    });
  },

  /**
   *
   * @param state
   * @param materialId
   * @returns {*|Object}
   */
  addMaterialToChosenMaterials: function addMaterialsToChosenMaterials(state, materialId) {
    var clonedState = sw.utils.cloneDataObject(state);

    clonedState.chosenMaterials.push({
      id: materialId,
      materialTitle: clonedState.materials[materialId].title,
      isDefault: !_.keys(clonedState.chosenMaterials).length
    });

    return clonedState.chosenMaterials;
  },

  /**
   * @param  {object} currentAttrs Current state.
   * @param  {object} action Action type and payload (materialId).
   * @return {object} Returns pconfs with the addition of this partial that will be filled in
   * once the AJAX call returns and triggers actionCreators.modelMaterialSelected.
   */
  getMaterialSelectedInfo: function getMaterialSelectedInfo(currentAttrs, action) {
    var state = sw.utils.cloneDataObject(currentAttrs);

    // We want to make sure the current pconfs + the ones being added are less than the pconf limit.
    if ((state.productConfigurations.length + state.chosenVariantOptions.length) > PCONF_LIMIT) {
      sw.notifications.error(sw.data.productEditErrorMsgs.maxVariantError);
      return;
    }

    var chosenMaterialIds = _.pluck(state.chosenMaterials, 'id');
    var materialIds = action.payload.materialId.split(',');

    var newMaterialIds = _.difference(materialIds, chosenMaterialIds);

    if (this.getBaseModel(state.models)) {
      this.dispatchMaterialSelected(state, newMaterialIds);
    }

    if (!_.isEmpty(state.chosenVariantOptions)) {
      _.each(newMaterialIds, function(materialId) {
        state.chosenMaterials = this.addMaterialToChosenMaterials(state, materialId);

        _.each(state.chosenVariantOptions, function(option) {
          state = this.addPendingPconfRow(state, materialId, option);
        }, this);

      }, this);
    } else {
      _.each(newMaterialIds, function(materialId) {
        state = this.addPendingPconfRow(state, materialId);
        state.chosenMaterials = this.addMaterialToChosenMaterials(state, materialId);
      }, this);
    }

    state.chosenMaterials = this.sortChosenMaterials(state);

    state.productConfigurations = this.sortPconfs(state);

    return {
      productConfigurations:  state.productConfigurations,
      chosenMaterials:        state.chosenMaterials,
      pendingPconfIndex:      state.pendingPconfIndex
    };
  },

  /**
   * Sort product configurations by variant options, priority, and material ID.
   * @param state {object}
   * @returns {object} ordered pconfs.
   */
  sortPconfs: function sortPconfs(state) {
    var clonedState = sw.utils.cloneDataObject(state);

    var pconfArray = _.toArray(clonedState.productConfigurations);

    var sortByVariants = clonedState.groupBy === VARIANT_GROUPING;
    var orderedPconfs = pconfArray.sort(function(a, b) {
      var optionA, optionB, optionDiff;
      if (clonedState.chosenVariantOptions.length) {
        optionA = _.findWhere(clonedState.chosenVariantOptions, {id: a.variantOptionId});
        optionB = _.findWhere(clonedState.chosenVariantOptions, {id: b.variantOptionId});
        optionDiff = optionA.priority - optionB.priority;
      }
      var materialDiff = clonedState.materialOrder.indexOf(a.materialId) - clonedState.materialOrder.indexOf(b.materialId);

      // When sorting by variants, we want the option diff to be the sorting priority, and material diff more materials.
      var variantsSort = optionDiff || materialDiff;
      var materialSort = materialDiff || optionDiff;

      return sortByVariants ? variantsSort : materialSort;
    });

    return orderedPconfs;
  },

  sortChosenMaterials: function sortChosenMaterials(state) {
    var clonedState = sw.utils.cloneDataObject(state);

    clonedState.chosenMaterials.sort(function(a, b) {
      return clonedState.materialOrder.indexOf(a.id) - clonedState.materialOrder.indexOf(b.id);
    });

    return clonedState.chosenMaterials;
  },

  /**
   * @param  {object} currentAttrs Current state.
   * @param  {object} action Action type and payload(materialId, pconf)
   * @return {object} Returns updated pconfs and chosen materials.
   */
  materialSelected: function materialSelected(currentAttrs, action) {
    var state = sw.utils.cloneDataObject(currentAttrs);

    // If there are variant options selected, we want a new pconf for each option.
    if (!_.isEmpty(state.chosenVariantOptions)) {
      _.each(state.chosenVariantOptions, function(option) {
        _.each(action.payload.responseInfo, function(responseInfo) {
          state = this.updatePartialPconfRow(state, responseInfo.pconfData, responseInfo.pconfData.materialId, option.id);
        }, this);
      }, this);
    } else {
      _.each(action.payload.responseInfo, function(responseInfo) {
        state = this.updatePartialPconfRow(state, responseInfo.pconfData, responseInfo.pconfData.materialId);
      }, this);
    }

    if (!this.hasDefaultPconf(state.productConfigurations)) {
      state = this.setDefaults(state);
    }

    return {
      productConfigurations:  state.productConfigurations,
      chosenVariantOptions:   state.chosenVariantOptions
    };
  },

  /**
   * @param  {object} currentAttrs Current state.
   * @param  {object} action Action type and payload (materialId)
   * @return {object} Returns the updated pconfs and chosen materials.
   */
  materialDeselected: function materialDeselected(currentAttrs, action) {
    var state = sw.utils.cloneDataObject(currentAttrs);

    var currentDefaultPconf = sw.utils.cloneDataObject(_.findWhere(state.productConfigurations, {isDefault: true}));

    state.productConfigurations = _.reduce(state.productConfigurations, function(updatedPconfs, pconf) {
      if (parseInt(pconf.materialId, 10) !== parseInt(action.payload.materialId, 10)) {
        updatedPconfs.push(pconf);
      }

      return updatedPconfs;
    }, []);

    state.chosenMaterials = _.reject(state.chosenMaterials, function(chosenMaterial) {
      if (chosenMaterial.id === action.payload.materialId) {
        return chosenMaterial;
      }
    });

    state.chosenMaterials = this.sortChosenMaterials(state);

    if (!this.hasDefaultPconf(state.productConfigurations)) {
      state = this.setDefaults(state, {variantOptionId: currentDefaultPconf.variantOptionId});
    }

    return {
      productConfigurations:  state.productConfigurations,
      chosenMaterials:        state.chosenMaterials,
      chosenVariantOptions:   state.chosenVariantOptions
    };
  },

  /**
   * @param  {object} currentAttrs Current state.
   * @param  {object} action Action type and payload (only action type required)
   * @return {object} Returns the updated pconfs.
   */
  getModelSelectedInfo: function getModelSelectedInfo(currentAttrs, action) {
    var state = sw.utils.cloneDataObject(currentAttrs);

    // Format for sending info to the endpoint.
    var requestData = {
      validationInfo: {},
      spin: state.spin,
      contentLicenseId: state.productContentLicense.licenseId,
      contentLicenseAccepted: state.productContentLicense.isAccepted
    };

    _.each(action.payload.validationInfo, function(validationInfo) {
      requestData.validationInfo[validationInfo.pconfId] = {
        materialId: validationInfo.materialId,
        modelId:    validationInfo.modelId,
        pconfId:    validationInfo.pconfId
      };

    }, this);

    this.getInfoAjax(requestData, function(responseData) {
      sw.dispatch(actionCreators.modelSelected(responseData));
    });
  },

  /**
   *
   * @param currentAttrs
   * @param action
   * @returns {{productConfigurations: array, models: object}}
   */
  modelSelected: function modelSelected(currentAttrs, action) {
    var state = sw.utils.cloneDataObject(currentAttrs);

    _.each(action.payload.responseInfo, function(responseInfo) {
      var pconfData = responseInfo.pconfData;
      var modelPconf = _.findWhere(state.productConfigurations, {pconfId: pconfData.pconfId});

      _.extend(modelPconf, {
        modelId:                    pconfData.modelId,
        modelName:                  pconfData.modelName,
        basePrice:                  pconfData.basePrice,
        totalPrice:                 pconfData.totalPrice,
        isPrintable:                pconfData.isPrintable,
        modelDropdownOptions:       pconfData.modelDropdownOptions,
        canCustomizeModels:         pconfData.canCustomizeModels,
        customMakerComponentUrl:    pconfData.customMakerComponentUrl,
        hasShop:                    pconfData.hasShop,
        markupPrice:                pconfData.markupPrice,
        inactiveReason:             pconfData.inactiveReason,
        disabledModelChooser:       pconfData.disabledModelChooser,
        disabledRowSection:         pconfData.disabledRowSection,
        royaltyFee:                 pconfData.royaltyFee,
        hasAcceptedContentLicense:  state.productContentLicense.isAccepted === 1
      });

      var pconfIndex = this.getPconfIndex(state.productConfigurations, modelPconf.materialId, modelPconf.variantOptionId);
      state.productConfigurations[pconfIndex] = modelPconf;

      if (!state.models[responseInfo.modelData.id]) {
        state.models[responseInfo.modelData.id] = responseInfo.modelData;
      }
    }, this);

    return {
      productConfigurations: state.productConfigurations,
      models: state.models
    };
  },

  /**
   * @param  {object} currentAttrs Current state.
   * @param  {object} action Action type and payload (only action type required)
   * @return {object} Returns the changes in state.
   */
  variantSelected: function variantSelected(currentAttrs, action) {
    var state = sw.utils.cloneDataObject(currentAttrs);

    _.each(state.productConfigurations, function (pconf) {
      pconf.variantId = action.payload.chosenVariant;
    });

    sw.dispatch(productDiscoverabilityActionCreators.variantChanged(action.payload.chosenVariant));

    return {
      chosenVariant:          action.payload.chosenVariant,
      productConfigurations:  state.productConfigurations,
      currentVariantOptions:  state.variants[action.payload.chosenVariant].sortedOptions
    };
  },

  /**
   * @param  {object} currentAttrs Current state.
   * @return {object} Returns the changes in state.
   */
  variantDeselected: function variantDeselected(currentAttrs) {
    var state = sw.utils.cloneDataObject(currentAttrs);

    state.chosenVariant = null;
    var materialIds = _.pluck(state.chosenMaterials, 'id');

    state.productConfigurations = _.reduce(state.productConfigurations, function(updatedPconfs, pconf) {
      if (_.contains(materialIds, pconf.materialId)) {
        pconf.variantId = '';
        pconf.variantOptionId = '';
        pconf.variantOptionName = '';
        pconf.pconfId = 'pending_' + state.pendingPconfIndex;

        state.pendingPconfIndex += 1;

        updatedPconfs[pconf.pconfId] = pconf;

        materialIds = _.without(materialIds, pconf.materialId);
      }

      return updatedPconfs;
    }, {});

    state.chosenVariantOptions = [];
    state.productConfigurations = this.sortPconfs(state);

    state.groupBy = VARIANT_GROUPING;
    state.productConfigurations = this.sortPconfs(state);

    sw.dispatch(productDiscoverabilityActionCreators.variantChanged(null));

    return {
      chosenVariant: state.chosenVariant,
      chosenVariantOptions:  state.chosenVariantOptions,
      productConfigurations: state.productConfigurations,
      currentVariantOptions: {},
      groupBy:               state.groupBy
    };
  },

  /**
   * Makes an Ajax request and dispatches the response info.
   *
   * @param {object} state
   * @param {object} material
   * @param {object} variantOption
   */
  dispatchVariantOptionSelected: function dispatchVariantOptionSelected(state, variantOption) {
    var baseModel = this.getBaseModel(state.models);
    var materialIds = _.pluck(state.chosenMaterials, 'id');

    var chosenVariants = {};
    if (!_.isEmpty(state.chosenVariant)) {
      chosenVariants[state.chosenVariant] = {
        chosenVariantOptionIds:   variantOption.id
      };
    }

    // Format for sending request data to endpoint.
    var requestData = {
      validationInfo: {},
      spin: state.spin,
      contentLicenseId: state.productContentLicense.licenseId,
      contentLicenseAccepted: state.productContentLicense.isAccepted
    };

    _.each(materialIds, function(materialId) {
      requestData.validationInfo[materialId] = {
        materialId:             materialId,
        modelId:                baseModel.id,
        chosenVariants:         chosenVariants
      };
    }, this);

    this.getInfoAjax(requestData, function(responseData) {
      sw.dispatch(actionCreators.variantOptionSelected(variantOption, responseData));
    });
  },

  /**
   * When there are materials that need to be updated but no checks need to occur.
   */
  updateInitialVariantOption: function updateInitialVariantOption(state, variantOption) {
    var clonedState = sw.utils.cloneDataObject(state);

    var pconfArray = [];
    _.each(clonedState.productConfigurations, function(pconf) {
      var pconfInfo = {};
      pconfInfo.pconfData = pconf;

      pconfInfo.pconfData.variantOptionId = variantOption.id;
      pconfInfo.pconfData.variantOptionName = variantOption.variantTitle;
      pconfInfo.pconfData.pconfId = 'pending_' + clonedState.pendingPconfIndex;

      clonedState.pendingPconfIndex += 1;

      pconfArray.push(pconfInfo);
    });

    sw.dispatch(actionCreators.variantOptionSelected(variantOption, pconfArray));

    return clonedState;
  },

  /**
   * Adds an option to the variant option list.
   *
   * @param {object} state Current state.
   * @param {object} variantOption Option to be added.
   * @param {bool} isInitial Whether or not it's the initial option.
   * @returns {Object} Updated option list.
   */
  addChosenVariantOption: function addChosenVariantOption(state, variantOption, isInitial) {
    var clonedState = sw.utils.cloneDataObject(state);

    // If initial variant option, it's the default.
    variantOption.isDefault = isInitial;

    clonedState.chosenVariantOptions.push(variantOption);

    return _.sortBy(clonedState.chosenVariantOptions, 'priority');
  },

  /**
   * Adds a pending product configuration row to the state's product configurations and updates
   * the pending pconf index accordingly.
   *
   * @param state object
   * @param materialId string
   * @param variantOption object
   * @returns {*|Object} cloned state
   */
  addPendingPconfRow: function addPendingPconfRow(state, materialId, variantOption) {
    var clonedState = sw.utils.cloneDataObject(state);

    clonedState.pendingPconfIndex += 1;

    var variantOptionId = variantOption ? variantOption.id : '';
    var partialPconf = this.generatePendingPconf(clonedState, materialId, variantOptionId);

    if (variantOptionId) {
      partialPconf.variantOptionId = variantOptionId;
      partialPconf.variantOptionName = variantOption.variantTitle;
    }

    clonedState.productConfigurations.push(partialPconf);

    return clonedState;
  },

  /**
   * @param  {object} currentAttrs Current state.
   * @param  {object} action Action type and payload (only action type required)
   * @return {object} Returns the changes in state.
   */
  getVariantOptionInfo: function getVariantOptionInfo(currentAttrs, action) {
    var state = sw.utils.cloneDataObject(currentAttrs);

    // We want to make sure the current pconfs + the ones being added are less than the pconf limit.
    if ((state.productConfigurations.length + state.chosenMaterials.length) > PCONF_LIMIT) {
      sw.notifications.error(sw.data.productEditErrorMsgs.maxVariantError);
      return;
    }

    var variantOption = {
      id:             action.payload.variantOption,
      variantTitle:   state.variants[state.chosenVariant].options[action.payload.variantOption].title,
      isDefault:      !!state.chosenVariantOptions.length,
      priority:       state.variants[state.chosenVariant].options[action.payload.variantOption].priority
    };

    // When there are no previously chosen variant options, we just want to update all the current materials instead of adding new rows.
    var isInitial = _.isEmpty(state.chosenVariantOptions);

    state.chosenVariantOptions = this.addChosenVariantOption(state, variantOption, isInitial);

    if (!_.contains(state.chosenVariantOptions, action.payload.variantOption) &&  !_.isEmpty(state.chosenMaterials)) {

      if (isInitial) {
        state = this.updateInitialVariantOption(state, variantOption);
      } else {
        var materialIds = _.pluck(state.chosenMaterials, 'id');
        if (this.getBaseModel(state.models)) {
          this.dispatchVariantOptionSelected(state, variantOption);
        }

        _.each(materialIds, function(materialId) {
          state = this.addPendingPconfRow(state, materialId, variantOption);
        }, this);
      }
    } else {
      sw.dispatch(actionCreators.variantOptionSelected(variantOption, {}));
    }

    state.productConfigurations = this.sortPconfs(state);

    return {
      productConfigurations:      state.productConfigurations,
      chosenVariantOptions:       state.chosenVariantOptions,
      pendingPconfIndex:          state.pendingPconfIndex
    };
  },

  /**
   *
   * @param currentAttrs
   * @param action
   * @returns {{productConfigurations: *, chosenVariantOptions: *}}
   */
  variantOptionSelected: function variantOptionSelected(currentAttrs, action) {
    var state = sw.utils.cloneDataObject(currentAttrs);

    // We only need to worry about updating pconfs when materials are already chosen.
    if (!_.isEmpty(state.chosenMaterials)) {
      _.each(action.payload.responseInfo, function(responseInfo) {
        state = this.updatePartialPconfRow(state, responseInfo.pconfData, responseInfo.pconfData.materialId, action.payload.variantOption.id);
      }, this);
    }

    return {
      productConfigurations:  state.productConfigurations
    };
  },

  /**
   * @param  {object} currentAttrs Current state.
   * @param  {object} action Action type and payload (only action type required)
   * @return {object} Returns the changes in state.
   */
  variantOptionDeselected: function variantOptionDeselected(currentAttrs, action) {
    var state = sw.utils.cloneDataObject(currentAttrs);

    var currentDefaultPconf = sw.utils.cloneDataObject(_.findWhere(state.productConfigurations, {isDefault: true}));

    state.chosenVariantOptions = _.reject(state.chosenVariantOptions, function(chosenVariant) {
      if (chosenVariant.id === action.payload.variantOptionId) {
        return chosenVariant;
      }
    });

    var emptyChosenVariants = _.isEmpty(state.chosenVariantOptions);

    if (emptyChosenVariants) {
      state.groupBy = VARIANT_GROUPING;

      state.productConfigurations = this.sortPconfs(state);
    }

    state.productConfigurations = _.reduce(state.productConfigurations, function(updatedPconfs, pconf) {
      if (emptyChosenVariants) {
        pconf.variantOptionId = '';
        pconf.variantOptionName = '';
        pconf.pconfId = 'pending_' + state.pendingPconfIndex;

        state.pendingPconfIndex += 1;
        updatedPconfs.push(pconf);
      } else if (parseInt(pconf.variantOptionId, 10) !== parseInt(action.payload.variantOptionId, 10)) {
        updatedPconfs.push(pconf);
      }

      return updatedPconfs;
    }, []);

    if (!this.hasDefaultPconf(state.productConfigurations)) {
      state = this.setDefaults(state, {materialId: currentDefaultPconf.materialId});
    }

    return {
      productConfigurations:   state.productConfigurations,
      chosenVariantOptions:    state.chosenVariantOptions,
      chosenMaterials:         state.chosenMaterials,
      groupBy:                 state.groupBy
    };
  },

  /**
   * @param  {object} currentAttrs Current state.
   * @param  {object} action Action type and payload (only action type required)
   * @return {object} Returns the changes in state.
   */
  pconfActivationChanged: function pconfActivationChanged(currentAttrs, action) {
    var state = sw.utils.cloneDataObject(currentAttrs);

    _.each(state.productConfigurations, function(pconf) {
      if (pconf.pconfId === action.payload.pconfId) {
        pconf.forSale = action.payload.forSale;
        pconf.disabledRowSection = !action.payload.forSale;
        pconf.disabledModelChooser = !action.payload.forSale;
      }
    });

    return {
      productConfigurations:   state.productConfigurations
    };
  },

  /**
   * @param  {object} currentAttrs Current state.
   * @param  {object} action Action type and payload (materialId)
   * @return {object} Returns the changes in state (updated pConfs)
   */
  pconfDefaultChanged: function pconfDefaultChanged(currentAttrs, action) {
    var state = sw.utils.cloneDataObject(currentAttrs);

    var oldDefault = _.findWhere(state.productConfigurations, {isDefault: true});

    oldDefault.isDefault = false;

    var newDefault = _.find(state.productConfigurations, function(pconf) {
      var materialId = action.payload.defaultData.defaultMaterialRadio;
      var materialMatch = materialId === pconf.materialId;

      var variantOptionId = action.payload.defaultData.defaultVariantRadio;
      var variantMatch = variantOptionId === pconf.variantOptionId;

      if (!materialId) {
        return variantMatch;
      }

      if (!variantOptionId) {
        return materialMatch;
      }

      return variantMatch && materialMatch;
    });

    newDefault.isDefault = true;

    return {
      productConfigurations:   state.productConfigurations
    };
  },

  /**
   * Updates pconf when pricing changes.
   *
   * @param currentAttrs Current state.
   * @param action Action type and payload (markupPrice, totalPrice, pconfId)
   * @returns {{productConfigurations: *}} Returns all the pconfs with new pricing for the selected pconf.
   */
  pconfPricingChanged: function pconfPricingChanged(currentAttrs, action) {
    var state = sw.utils.cloneDataObject(currentAttrs);

    var pconf = _.findWhere(state.productConfigurations, {pconfId: action.payload.pconfId});

    _.extend(pconf, {
      markupPrice: action.payload.markupPrice,
      royaltyFee: action.payload.royaltyPrice,
      totalPrice:  action.payload.totalPrice
    });

    return {
      productConfigurations:  state.productConfigurations
    };
  },

  /**
   * Handles a change in the grouping of the material pricing table.
   *
   * @param currentAttrs object
   * @param action object Action type and payload (groupBy)
   * @returns {{groupBy: bool, productConfigurations: object}}
     */
  groupByChanged: function groupByChanged(currentAttrs, action) {
    var state = sw.utils.cloneDataObject(currentAttrs);

    state.groupBy = action.payload.groupBy;
    var sortedPconfs = this.sortPconfs(state);

    return {
      groupBy: state.groupBy,
      productConfigurations: sortedPconfs
    };
  },

  /**
   * Updates product content license info and pconf info once a content license has been added or changed.
   *
   * @param currentAttrs
   * @param action
   * @returns {{productContentLicense: (ProductEditModel.defaults.productContentLicense|{}|*)}}
   */
  contentLicenseChanged: function contentLicenseChanged(currentAttrs, action) {
    var state = sw.utils.cloneDataObject(currentAttrs);

    var pricingRule = {};
    if (action.payload.isAccepted === 1) {
      pricingRule = state.contentLicenses[action.payload.licenseId].pricingRule;
    }

    if (action.payload.isAccepted !== state.productContentLicense.isAccepted) {
      state.productConfigurations.forEach(function(pconf) {
        var pricing = swPricing.getPricing(pconf.basePrice, pconf.totalPrice, pricingRule);

        pconf.royaltyFee = pricing.royalty;
        pconf.totalPrice = pricing.totalPrice;
        pconf.markupPrice = pricing.markup;

        pconf.hasAcceptedContentLicense = action.payload.isAccepted === 1;
      });
    }

    // We want to be able to choose whether we accept or opt out if this is the first license
    state.resetAcceptance = action.payload.isAccepted === null ? 1 : 0;

    state.productContentLicense.isAccepted = action.payload.isAccepted;
    state.productContentLicense.licenseId = action.payload.licenseId;

    return {
      productContentLicense: state.productContentLicense,
      productConfigurations: state.productConfigurations,
      resetAcceptance:       state.resetAcceptance
    };
  },

  /**
   * If there's a content license tag that's entered and no product license has been selected or a different
   * product license has been selected, this will dispatch contentLicenseChanged.
   *
   * @param currentAttrs obj
   * @param action obj
   */
  contentLicenseTagEntered: function contentLicenseTagEntered(currentAttrs, action) {
    var state = sw.utils.cloneDataObject(currentAttrs);

    if (_.isEmpty(currentAttrs.productContentLicense) ||
      (!_.isEmpty(currentAttrs.productContentLicense) && currentAttrs.productContentLicense.licenseId !== action.payload.licenseId)) {
      sw.dispatch(actionCreators.contentLicenseChanged(action.payload.licenseId, null));
    }

    // If a tag is added, we need to add to content license tag IDs so we can keep track of content licenses
    // associated with tags
    if (state.tagContentLicenseIds.indexOf(action.payload.licenseId) === -1) {
      state.tagContentLicenseIds.push(action.payload.licenseId);
    }

    return {
      tagContentLicenseIds: state.tagContentLicenseIds
    };
  },

  /**
   * Sets the reset status for a user to choose whether they opt in or out again.
   *
   * @param currentAttrs
   * @param action
   * @returns {{resetAcceptance: *}}
   */
  resetAcceptance: function resetAcceptance(currentAttrs, action) {
    return {
      resetAcceptance: action.payload.resetAcceptance
    };
  }
});

module.exports = ProductEditModel;
